import '@mdxeditor/editor/style.css'

import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import CloseIcon from 'components/ReusableComponents/CloseIcon'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import ContentIcon from 'components/ReusableComponents/Icons/Content'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { ColorInput, ImageFileInput } from 'features/ConfigureWidgets'
import {
  editableSectionNames,
  propertyNames,
  propertyTypes,
  sectionsToProperties
} from 'features/EmailEditor/constants'
import { useEmailEditor } from 'features/EmailEditor/EmailEditorContext'
import { EmailInboxPreview } from 'features/EmailEditor/EmailPreview'
import RichTextEditor from 'features/EmailEditor/RichTextEditor'
import { getTemplateOptions } from 'features/EmailEditor/templates'
import { useEffect, useState } from 'react'
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark'

import styles from './EmailEditor.module.scss'

const PanelInner = ({
  onClose,
  onDiscard,
  setEditingSection,
  editingSection,
  sections,
  sectionControls,
  onSaveAndClose,
  handleSectionChange
}) => {
  const { contentIndex } = useEmailEditor()

  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)

  const [lastContentIndex] = useState(contentIndex)

  const hasChanges = lastContentIndex !== contentIndex

  function handleClickClose() {
    if (hasChanges) {
      setIsDiscardDialogOpen(true)
    } else {
      onClose()
    }
  }

  return (
    <div className={styles.innerDialog}>
      {isDiscardDialogOpen && (
        <ConfirmationDialog
          title="Discard Changes?"
          positiveMessage="Discard"
          negativeMessage="Go back"
          hasDangerousAction={true}
          onClose={() => {
            setIsDiscardDialogOpen(false)
          }}
          onConfirm={() => {
            onDiscard()
            setIsDiscardDialogOpen(false)
            setEditingSection('inbox-preview')
          }}
        >
          <p style={{ margin: 0 }}>Are you sure you want to discard your changes?</p>
        </ConfirmationDialog>
      )}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.headerTitle}>Editing email</span>
          <button className={styles.closeButton} onClick={handleClickClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.tabsHolder}>
          <div className={styles.tabs}>
            {sections.map(section => (
              <button
                key={section}
                onClick={() => handleSectionChange(section)}
                className={section === editingSection ? styles.activeTab : styles.tab}
              >
                <span className={styles.paginatorNo}>
                  {editableSectionNames[section]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.inputsHeader}>
        <p className={style.sectionTitle}>{editableSectionNames[editingSection]}</p>
      </div>
      <div className={styles.controlsHolder}>{sectionControls()}</div>
      <div className={styles.footer}>
        <SecondaryRegularButton onClick={handleClickClose}>
          {hasChanges ? 'Discard changes' : 'Go back'}
        </SecondaryRegularButton>
        <PrimaryRegularButton
          onClick={() => {
            onSaveAndClose()
            setIsDiscardDialogOpen(false)
            setEditingSection('inbox-preview')
          }}
        >
          Save and close
        </PrimaryRegularButton>
      </div>
    </div>
  )
}

const EmailEditorPanel = ({ onSaveAndClose, onClose }) => {
  const {
    editingSection,
    setEditingSection,
    selectedTemplate,
    hoveredPreview,
    setHoveredPreview,
    mousePosition,
    setMousePosition,
    emailState,
    updateEmailState,
    updateRichTextField,
    handleTemplateChange,
    handleSocialMediaChange,
    sections
  } = useEmailEditor()

  const hasChanges = true

  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)

  function handleClickClose() {
    if (hasChanges) {
      setIsDiscardDialogOpen(true)
    } else {
      onClose()
    }
  }

  // Function to scroll to a section in the preview
  const scrollToSection = sectionName => {
    const targetElement = document.querySelector(`.mjml-${sectionName}`)
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })

      // Add highlight effect
      document.querySelectorAll(`.${styles.highlightedSection}`).forEach(el => {
        el.classList.remove(styles.highlightedSection)
      })
      targetElement.classList.add(styles.highlightedSection)
    }
  }

  // Enhanced setEditingSection that includes scrolling
  const handleSectionChange = sectionName => {
    setEditingSection(sectionName)
    if (sectionName !== 'inbox-preview') {
      // Small delay to ensure the preview is rendered before scrolling
      setTimeout(() => scrollToSection(sectionName), 100)
    }
  }

  useEffect(() => {
    if (!editingSection || editingSection === 'inbox-preview') {
      scrollToSection('header')
    }
  }, [editingSection])

  const sectionControls = () => (
    <>
      {sectionsToProperties[editingSection].map(property => {
        const type = propertyTypes[property]
        if (type === 'color') {
          return (
            <div key={property} className={styles.inputGroup}>
              <ColorInput
                title={propertyNames[property]}
                height={32}
                defaultColor={emailState[property]}
                onChange={value => updateEmailState(property, value)}
              />
            </div>
          )
        } else if (type === 'socialmedia') {
          return (
            <div key={property} className={styles.inputGroup}>
              <span
                style={{
                  display: 'inline-block',
                  width: '200px',
                  color: '#697474',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '10px'
                }}
              >
                {propertyNames[property]}
              </span>
              {emailState.socialMediaLinks.map((link, index) => (
                <TextInput
                  key={index}
                  label={link.platform}
                  value={link.url}
                  onChange={e => handleSocialMediaChange(index, 'url', e.target.value)}
                />
              ))}
            </div>
          )
        } else if (type === 'image') {
          return (
            <ImageFileInput
              kind="drip-emails"
              title={propertyNames[property]}
              backgroundSize={property.includes('logo') ? '90%' : 'cover'}
              defaultUrl={emailState[property] || ''}
              onChange={text => {
                updateEmailState(property, text)
              }}
              minWidth={200}
              minHeight={200}
            />
          )
        } else if (type === 'richtext') {
          return (
            <div key={property} className={styles.inputGroup}>
              <RichTextEditor
                label={propertyNames[property]}
                value={emailState.richtextFields[property] || ''}
                onChange={value => updateRichTextField(property, value)}
              />
            </div>
          )
        } else if (type === 'multilinetext') {
          return (
            <div key={property} className={styles.inputGroup}>
              <TextArea
                label={propertyNames[property]}
                value={emailState.preheaderText}
                onChangeValue={value => updateEmailState(property, value)}
                multiline={true}
              />
            </div>
          )
        } else {
          // text
          return (
            <div key={property} className={styles.inputGroup}>
              <TextInput
                label={propertyNames[property]}
                value={emailState[property]}
                onChangeValue={text => {
                  updateEmailState(property, text)
                }}
              />
            </div>
          )
        }
      })}
    </>
  )

  if (editingSection === 'inbox-preview') {
    return (
      <div className={styles.inputs}>
        {editingSection && editingSection !== 'inbox-preview' && (
          <div className={styles.inputsHeader}>
            <p className={style.sectionTitle}>{editableSectionNames[editingSection]}</p>
          </div>
        )}
        <div className={styles.inputsInner}>
          {/* Template Gallery - only show when not editing a specific section */}
          {editingSection === 'inbox-preview' && (
            <div className={styles.inputGroup}>
              <label
                style={{
                  fontSize: '12px',
                  height: '18px',
                  lineHeight: '12px',
                  marginBottom: 5,
                  color: '#888888'
                }}
              >
                Email Template
              </label>
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '12px'
                }}
              >
                {getTemplateOptions().map(option => (
                  <div
                    key={option.value}
                    onClick={() => handleTemplateChange(option.value)}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedTemplate === option.value
                          ? '3px solid #0079ff'
                          : '2px solid #ddd',
                      borderRadius: '12px',
                      padding: '0',
                      backgroundColor:
                        selectedTemplate === option.value ? '#f0f8ff' : '#fff',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      boxShadow:
                        selectedTemplate === option.value
                          ? '0 4px 12px rgba(0, 121, 255, 0.2)'
                          : '0 2px 4px rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 160,
                      marginBottom: 10
                    }}
                    onMouseEnter={e => {
                      setHoveredPreview(option.preview)
                      setMousePosition({ x: e.clientX, y: e.clientY })
                      if (selectedTemplate !== option.value) {
                        e.currentTarget.style.borderColor = '#0079ff'
                        e.currentTarget.style.backgroundColor = '#f9f9f9'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                    onMouseMove={e => {
                      if (hoveredPreview) {
                        setMousePosition({ x: e.clientX, y: e.clientY })
                      }
                    }}
                    onMouseLeave={e => {
                      setHoveredPreview(null)
                      setMousePosition({ x: 0, y: 0 })
                      if (selectedTemplate !== option.value) {
                        e.currentTarget.style.borderColor = '#ddd'
                        e.currentTarget.style.backgroundColor = '#fff'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <img
                      src={option.cardImage}
                      alt={option.label}
                      style={{
                        width: '100%',
                        height: '86px',
                        objectFit: 'cover',
                        borderRadius: '12px 12px 0 0',
                        border: 'none'
                      }}
                      onError={e => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.height = '140px'
                        e.target.nextSibling.style.display = 'flex'
                        e.target.nextSibling.style.alignItems = 'center'
                        e.target.nextSibling.style.justifyContent = 'center'
                        e.target.nextSibling.style.backgroundColor = '#f5f5f5'
                        e.target.nextSibling.style.borderRadius = '12px 12px 0 0'
                      }}
                    />
                    <div
                      style={{
                        display: 'none',
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      Preview Not Available
                    </div>
                    <div style={{ padding: '12px', textAlign: 'left' }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: selectedTemplate === option.value ? 'bold' : '600',
                          color: selectedTemplate === option.value ? '#0079ff' : '#333'
                        }}
                      >
                        {option.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Editor Inputs according to the active section */}
          {sectionControls()}
          {editingSection === 'inbox-preview' && (
            <>
              <label
                style={{
                  fontSize: '12px',
                  height: '18px',
                  lineHeight: '12px',
                  marginBottom: 0,
                  marginTop: 10,
                  display: 'block',
                  color: '#888888'
                }}
              >
                Inbox preview
              </label>

              <EmailInboxPreview />
            </>
          )}
        </div>

        {/* Tooltip-style preview */}
        {hoveredPreview && (
          <div
            style={{
              position: 'fixed',
              left: mousePosition.x + 20,
              top: mousePosition.y + 20,
              zIndex: 9999,
              pointerEvents: 'none',
              backgroundColor: 'white',
              border: '2px solid #ddd',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              maxWidth: '50vw',
              maxHeight: '50vh'
            }}
          >
            <img
              src={hoveredPreview}
              alt="Template Preview"
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '500px',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '4px',
                display: 'block'
              }}
            />
          </div>
        )}
      </div>
    )
  } else if (editingSection) {
    return (
      <PanelInner
        isDiscardDialogOpen={isDiscardDialogOpen}
        setIsDiscardDialogOpen={setIsDiscardDialogOpen}
        handleClickClose={handleClickClose}
        onClose={() => setEditingSection('inbox-preview')}
        onDiscard={onClose}
        setEditingSection={setEditingSection}
        editingSection={editingSection}
        sections={sections}
        sectionControls={sectionControls}
        onSaveAndClose={onSaveAndClose}
        handleSectionChange={handleSectionChange}
      />
    )
  }
}

export default EmailEditorPanel
